import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Signature, ShieldCheck, Trash, PencilSimple, X, Check, Lock } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import SignaturePad from '../components/SignaturePad';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignaturePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [signature, setSignature] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const sigPadRef = useRef(null);

  const fetchSignature = async () => {
    try {
      const r = await axios.get(`${API}/owner/signature`, { withCredentials: true });
      setSignature(r.data?.signature || null);
      setUpdatedAt(r.data?.signature_updated_at || null);
    } catch {
      setSignature(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignature();
  }, []);

  const startEditing = () => {
    setEditing(true);
    // Give the pad time to mount
    setTimeout(() => sigPadRef.current?.clear(), 50);
  };

  const cancelEditing = () => {
    setEditing(false);
    sigPadRef.current?.clear();
  };

  const clearPad = () => sigPadRef.current?.clear();

  const saveSignature = async () => {
    if (sigPadRef.current?.isEmpty()) {
      toast.error('Disegna prima la firma');
      return;
    }
    const dataUrl = sigPadRef.current.toDataURL('image/png');
    setSaving(true);
    try {
      await axios.post(
        `${API}/owner/signature`,
        { signature: dataUrl },
        { withCredentials: true },
      );
      toast.success('Firma salvata e cifrata nel database');
      setSignature(dataUrl);
      setUpdatedAt(new Date().toISOString());
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel salvare la firma');
    } finally {
      setSaving(false);
    }
  };

  const deleteSignature = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/owner/signature`, { withCredentials: true });
      toast.success('Firma cancellata definitivamente — nessun recupero possibile');
      setSignature(null);
      setUpdatedAt(null);
      setConfirmDelete(false);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella cancellazione');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-zinc-500">Caricamento firma...</div>
    );
  }

  const hasSignature = !!signature && !editing;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-8" data-testid="signature-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center">
            <Signature size={22} weight="bold" className="text-teal-700" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 tracking-tight">Firma Host</h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              La firma che apponi automaticamente su contratti, ricevute affitto e ricevute imposta di soggiorno.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Security banner */}
      <div className="flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-100 p-4 mb-6">
        <ShieldCheck size={22} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <p className="font-bold">Firma cifrata a riposo (AES-128 + HMAC-SHA256)</p>
          <p className="text-emerald-800 text-xs mt-0.5">
            L'immagine viene salvata nel database solo dopo cifratura simmetrica Fernet.
            Alla cancellazione il blob cifrato viene sovrascritto due volte con dati casuali e poi rimosso:{' '}
            <strong>non è tecnicamente recuperabile</strong>.
          </p>
        </div>
      </div>

      {/* Current signature card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white border border-zinc-200 rounded-3xl overflow-hidden shadow-sm"
      >
        {hasSignature ? (
          <div className="p-6" data-testid="signature-preview-card">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700">
                <Check size={14} weight="bold" /> Firma attiva
              </span>
              {updatedAt && (
                <span className="text-xs text-zinc-400">
                  Ultimo aggiornamento: {new Date(updatedAt).toLocaleString('it-IT')}
                </span>
              )}
            </div>
            <div className="border border-dashed border-zinc-300 rounded-2xl bg-[#FAFAFA] p-4 flex items-center justify-center min-h-[180px]">
              <img
                src={signature}
                alt="Firma salvata"
                className="max-h-40 object-contain"
                data-testid="signature-image"
              />
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={startEditing}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold"
                data-testid="signature-edit-btn"
              >
                <PencilSimple size={16} weight="bold" className="mr-2" />
                Modifica firma
              </Button>
              <Button
                onClick={() => setConfirmDelete(true)}
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50 font-bold"
                data-testid="signature-delete-btn"
              >
                <Trash size={16} weight="bold" className="mr-2" />
                Elimina definitivamente
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6" data-testid="signature-editor-card">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500">
                <Signature size={14} weight="bold" />
                {signature ? 'Ridisegna la firma' : 'Disegna la tua firma'}
              </span>
              <button
                onClick={clearPad}
                className="text-xs text-zinc-500 hover:text-zinc-800"
                data-testid="signature-clear-btn"
              >
                Pulisci
              </button>
            </div>
            <div className="border-2 border-dashed border-teal-300 rounded-2xl bg-teal-50/20 overflow-hidden">
              <SignaturePad ref={sigPadRef} height={220} data-testid="signature-pad" />
            </div>
            <p className="text-xs text-zinc-500 mt-3">
              Disegna con il dito (mobile), il mouse o una penna touch. Verrà salvata come immagine PNG cifrata.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={saveSignature}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold"
                data-testid="signature-save-btn"
              >
                <Check size={16} weight="bold" className="mr-2" />
                {saving ? 'Salvataggio...' : 'Salva firma'}
              </Button>
              {signature && (
                <Button
                  onClick={cancelEditing}
                  variant="outline"
                  className="border-zinc-300 text-zinc-700 font-bold"
                  data-testid="signature-cancel-btn"
                >
                  <X size={16} weight="bold" className="mr-2" />
                  Annulla
                </Button>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="signature-delete-confirm"
          >
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <Trash size={24} weight="bold" className="text-red-600" />
            </div>
            <h2 className="text-xl font-black text-zinc-900 mb-2">Eliminare la firma?</h2>
            <p className="text-sm text-zinc-600 mb-2">
              Il blob cifrato verrà sovrascritto due volte con dati casuali e poi rimosso.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-3 mb-5 flex items-start gap-2">
              <Lock size={16} weight="bold" className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-900 font-bold">
                Operazione irreversibile: la firma NON sarà più recuperabile in nessun modo.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={deleteSignature}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white font-bold flex-1"
                data-testid="signature-delete-confirm-btn"
              >
                {deleting ? 'Eliminazione...' : 'Sì, elimina definitivamente'}
              </Button>
              <Button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                variant="outline"
                className="border-zinc-300 text-zinc-700 font-bold flex-1"
                data-testid="signature-delete-cancel-btn"
              >
                Annulla
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
